/**
 * The address book, for looking at and thinning out.
 *
 * The book fills itself from mail that arrives and mail that is sent, which is
 * the only way an address book is ever accurate -- but it means it also fills
 * with one-off senders, expired no-reply addresses and every list the user has
 * ever been added to. Harvesting without a way to prune is a suggestion list
 * that gets worse every month, so this is the other half of that feature rather
 * than an accessory to it.
 *
 * Two verbs, and the difference matters. "Always suggest" promotes an address
 * to the explicit tier, which keeps the counts it has already earned -- the
 * moment a user confirms a contact is the worst possible moment to throw away
 * the evidence that it matters. "Stop suggesting" is a prune, and for a
 * harvested address it is deliberately not a row delete on the server: a plain
 * delete would be undone by the next message from that person, so the prune
 * would appear to work and quietly reverse itself a day later.
 *
 * # Where it is opened from
 *
 * Settings, and it is `nav.suggestionsOpen` that says so -- a sheet reached
 * from a pane that is itself a layer has to be a layer too, or the phone's back
 * button closes Settings and leaves this hanging over the mail.
 *
 * Its home is Settings rather than the composer, because thinning the book is a
 * decision about the next year of suggestions and not about the message in
 * front of you -- and because it arrives as a full-screen list, which over
 * something half-written is the wrong thing to open. What the composer owes a
 * bad suggestion is the fix where it was noticed: the × on the row as it is
 * offered, and the Delete key that does the same, both in
 * `RecipientField.tsx`. Those two are a different affordance, not a shortcut to
 * this screen, and they stay whatever happens to the door in the composer.
 */

import { useEffect, useId, useState } from "preact/hooks";

import { plural } from "../lib/format";
import {
  listContacts,
  promoteContact,
  pruneContacts,
  removeContact,
  type Contact,
} from "../lib/contacts";
import * as store from "../lib/store";
import { useDialog } from "./dialog";
import * as Icon from "./Icons";

const PAGE = 60;

export function Suggestions({ onClose }: { onClose: () => void }) {
  const sheet = useDialog<HTMLDivElement>(onClose);
  const titleId = useId();
  const [items, setItems] = useState<Contact[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const page = await listContacts(PAGE, 0);
      setItems(page.items);
      setTotal(page.total);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Filtered here rather than by asking the server, because this list is one
  // page the browser already holds and a keystroke-per-request would be a
  // slower version of the same answer.
  const term = filter.trim().toLowerCase();
  const shown = term
    ? items.filter(
        (c) =>
          c.displayAddress.toLowerCase().includes(term) ||
          (c.name ?? "").toLowerCase().includes(term),
      )
    : items;

  async function prune(c: Contact): Promise<void> {
    setItems((list) => list.filter((x) => x.id !== c.id));
    try {
      await removeContact(c.id);
    } catch {
      store.toast("That suggestion could not be removed.", "error");
      void load();
    }
  }

  async function promote(c: Contact): Promise<void> {
    try {
      const updated = await promoteContact(c.address, c.name);
      setItems((list) => list.map((x) => (x.id === c.id ? updated : x)));
    } catch {
      store.toast("That address could not be pinned.", "error");
    }
  }

  async function pruneQuiet(): Promise<void> {
    setBusy(true);
    try {
      // Received-only and untouched for a year. Named explicitly rather than
      // offered as a free-form filter: the daemon refuses an unfiltered prune
      // precisely because one would empty the book, and a UI that can express
      // "everything" invites someone to try it at midnight.
      const { removed } = await pruneContacts({ tier: "received", olderThanDays: 365 });
      store.toast(
        removed === 0
          ? "Nothing was old enough to remove."
          : `Removed ${plural(removed, "suggestion")}.`,
      );
      await load();
    } catch (e) {
      store.toast(e instanceof Error ? e.message : "The prune failed.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="overlay" onClick={onClose}>
      <div
        ref={sheet}
        class="sheet cm"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="cm-head">
          <strong class="grow" id={titleId}>
            Recipient suggestions
          </strong>
          <button class="icon-btn" aria-label="Close suggestions" onClick={onClose}>
            <Icon.Close />
          </button>
        </div>

        <p class="lede">
          Built from mail you have sent and received. Removing one stops it being suggested; no
          message is touched.
        </p>

        <div class="cm-tools">
          <input
            type="search"
            value={filter}
            placeholder="Filter these"
            aria-label="Filter suggestions"
            onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
          />
          <button class="btn" disabled={busy} onClick={() => void pruneQuiet()}>
            Remove received-only, quiet for a year
          </button>
        </div>

        {error && <p class="error-note">{error}</p>}
        {loading && <span class="spinner" />}

        {!loading && !error && (
          <>
            <p class="cm-count">
              {total === null
                ? plural(shown.length, "suggestion")
                : `${shown.length} of ${plural(total, "suggestion")}`}
              {total !== null && total > items.length && ` — showing the first ${items.length}`}
            </p>
            <ul class="cm-list">
              {shown.map((c) => (
                <li key={c.id} class="cm-row">
                  <span class="cm-who">
                    {c.name && <strong>{c.name}</strong>}
                    <span class="cm-addr">{c.displayAddress}</span>
                  </span>
                  <span class={`cm-tier tier-${c.tier}`}>{c.tier}</span>
                  <button
                    class="btn ghost"
                    disabled={c.tier === "explicit"}
                    title={
                      c.tier === "explicit"
                        ? "Already always suggested"
                        : "Keep suggesting this address even when it goes quiet"
                    }
                    onClick={() => void promote(c)}
                  >
                    Always suggest
                  </button>
                  <button
                    class="btn danger ghost"
                    aria-label={`Stop suggesting ${c.displayAddress}`}
                    onClick={() => void prune(c)}
                  >
                    Remove
                  </button>
                </li>
              ))}
              {shown.length === 0 && (
                <li class="cm-empty">
                  {items.length === 0
                    ? "The address book is empty. It fills as mail arrives and as you send."
                    : "Nothing matches that filter."}
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
